import { useState, useEffect, useCallback, useRef } from 'react';
import { getTaskNotificationsBatch } from '../services/managerService';

export const useTaskNotifications = () => {
  const [notifications, setNotifications] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [requestedTasks, setRequestedTasks] = useState(new Set());

  // Use refs to avoid dependency issues
  const notificationsRef = useRef(notifications);
  const requestedTasksRef = useRef(requestedTasks);

  // Keep refs in sync with state
  useEffect(() => {
    notificationsRef.current = notifications;
  }, [notifications]);

  useEffect(() => {
    requestedTasksRef.current = requestedTasks;
  }, [requestedTasks]);

  const loadNotifications = useCallback(async (taskIds) => {
    // Filter out tasks that are already loaded or being requested
    const newTaskIds = taskIds.filter(id => 
      !notificationsRef.current[id] && !requestedTasksRef.current.has(id)
    );

    if (newTaskIds.length === 0) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Mark these tasks as being requested
      setRequestedTasks(prev => new Set([...prev, ...newTaskIds]));
      
      const batchData = await getTaskNotificationsBatch(newTaskIds);
      
      setNotifications(prev => ({
        ...prev,
        ...batchData
      }));
      
      // Remove from requested set
      setRequestedTasks(prev => {
        const newSet = new Set(prev);
        newTaskIds.forEach(id => newSet.delete(id));
        return newSet;
      });
      
    } catch (err) {
      console.error('Error loading task notifications batch:', err);
      setError('Failed to load notifications');
      
      // Remove from requested set on error
      setRequestedTasks(prev => {
        const newSet = new Set(prev);
        newTaskIds.forEach(id => newSet.delete(id));
        return newSet;
      });
    } finally {
      setLoading(false);
    }
  }, []); // Empty dependency array - no dependencies needed

  const getNotification = useCallback((taskId) => {
    return notifications[taskId] || null;
  }, [notifications]);

  const isTaskLoading = useCallback((taskId) => {
    return requestedTasks.has(taskId);
  }, [requestedTasks]);

  return {
    notifications,
    loading,
    error,
    loadNotifications,
    getNotification,
    isTaskLoading
  };
}; 